import APIUtils from '@spothero/utils/api';
import ObjectUtils from '@spothero/utils/object';
import ErrorUtils from 'utils/error-utils';
import Config from '@/config/index';
import SpotUtils from 'utils/spot';
import * as SearchUtils from '../search-v2/utils';
const { formatDistance } = SearchUtils;
/**
 * Get data for a given facility when its part of a given event package
 *
 * @function getFacilityDetail
 * @param {string} eventPackageId - Event package Id
 * @param {object} facilityId - Id of facility
 * @param {object} params - params to include with facilities call
 * @returns {Promise<EventFacilityDetail>} - API response
 * @see {@link https://spothero.com/api/v1/docs/endpoints/#!/EventPackages/get_facilities_facility_id_rates_event_packages_event_package_id|Documentation}
 */
export const getFacilityDetail = (eventPackageId, facilityId, params = {}) => {
    return APIUtils.get(`facilities/${facilityId}/rates/event-packages/${eventPackageId}/`, {
        include: Config.facilityIncludes,
        ...params,
    })
        .then(response => {
        try {
            const unformattedHoursOfOperation = response.data.data.facility.hours_of_operation;
            const facilityData = ObjectUtils.camelizeKeys(response.data.data);
            const { eventPackage, bulkEventRates, facility, currencyType, ratingInfo: { numberOfRatings: count, starRating: average }, operatorNotes, spotNotes, } = facilityData;
            const { eventCount } = eventPackage;
            // differences between spot detail and search response
            // images, event package data, total price, currency symbol and type
            const { title, distance, latitude, longitude, images, amenitiesFull, gettingHere, redemptionInstructions, restrictions, operatorId, city, state, cancellationAllowed, } = facility;
            // calculate total price
            const totalPrice = bulkEventRates.reduce((accmPrice, eventRate) => {
                return accmPrice + eventRate.displayPrice;
            }, 0);
            const transformedResult = {
                isV2: true,
                spotId: `${facilityId}`,
                title,
                selectedRate: {
                    fullUrl: `/checkout/${facilityId}/event-packages/${eventPackageId}/`,
                    advertisedPrice: {
                        currencyCode: currencyType.toUpperCase(),
                        value: totalPrice / eventCount,
                    },
                    totalPrice: {
                        currencyCode: currencyType.toUpperCase(),
                        value: totalPrice,
                    },
                    amenities: amenitiesFull?.map(amenity => ({
                        type: amenity.slug,
                        displayName: amenity.name,
                    })) || [],
                },
                rating: {
                    average,
                    count,
                },
                images: images.map(image => ({
                    id: image.id,
                    urlTemplate: image.urlTemplate,
                    url: '',
                    altText: `Photo of ${title}`,
                })),
                addresses: [
                    {
                        latitude,
                        longitude,
                        // dummy info
                        id: '',
                        city: '',
                        country: '',
                        postalCode: '',
                        state: '',
                        streetAddress: '',
                        timeZone: '',
                    },
                ],
                availability: {
                    available: true,
                    unavailableReasons: [],
                },
                distance: formatDistance(distance),
                facilityType: 'unknown',
                eventPackage: {
                    ...eventPackage,
                    events: eventPackage.events.map((event, index) => ({
                        ...event,
                        price: {
                            value: bulkEventRates[index].price,
                            priceBreakdown: bulkEventRates[index].priceBreakdown.items,
                            currencyCode: currencyType,
                        },
                    })),
                },
                facilityRestrictions: restrictions,
                // we leave access hours formatted according to v1 data structure
                // because the AccessHours component that displays this data
                // is tightly coupled to it
                accessHoursFormattedV1: SpotUtils.formatAccessHours(unformattedHoursOfOperation),
                navigationTip: gettingHere,
                redemptionInstructions,
                operatorNotes,
                adminNotes: spotNotes,
                operatorId,
                city,
                state,
                cancellationAllowed,
            };
            return transformedResult;
        }
        catch (error) {
            return Promise.reject(new Error('Response shape unexpected.'));
        }
    })
        .catch(err => ErrorUtils.transformAndReject(err));
};
